import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle, Clock, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listEventRegistrations, listEventInvitees, listEventInviteStatus } from "@/lib/api/admin";
import type { EventInvitee, EventRegistration, StoreEvent } from "@/lib/api/types";
import { formatDateTimeSmart } from "@/lib/utils/date";

const PAGE_SIZE = 20;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  event: StoreEvent | null;
};

export function GuestlistDialog({ open, onOpenChange, storeId, event }: Props) {
  const user = useAdminAuth();

  const [registrationsOffset, setRegistrationsOffset] = useState(0);
  const [inviteesOffset, setInviteesOffset] = useState(0);
  const [allRegistrations, setAllRegistrations] = useState<EventRegistration[]>([]);
  const [allInvitees, setAllInvitees] = useState<EventInvitee[]>([]);

  // Reset accumulated pages whenever the dialog opens (or opens for a different event).
  useEffect(() => {
    if (!open) return;
    setRegistrationsOffset(0);
    setInviteesOffset(0);
    setAllRegistrations([]);
    setAllInvitees([]);
  }, [open, event?.id]);

  const {
    data: registrationsData,
    isLoading: registrationsLoading,
    isFetching: registrationsFetching,
  } = useQuery({
    queryKey: ["event-registrations", storeId, event?.id, registrationsOffset],
    queryFn: () =>
      listEventRegistrations(user!, storeId, event!.id, {
        limit: PAGE_SIZE,
        offset: registrationsOffset,
      }),
    enabled: open && !!user && !!event,
  });

  const {
    data: inviteesData,
    isLoading: inviteesLoading,
    isFetching: inviteesFetching,
  } = useQuery({
    queryKey: ["event-invitees", storeId, event?.id, inviteesOffset],
    queryFn: () =>
      listEventInvitees(user!, storeId, event!.id, { limit: PAGE_SIZE, offset: inviteesOffset }),
    enabled: open && !!user && !!event,
  });

  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ["event-invite-status", storeId, event?.id],
    queryFn: () => listEventInviteStatus(user!, storeId, event!.id),
    enabled: open && !!user && !!event,
  });

  // Synced from `data`, not inside queryFn, so a cache hit on remount still repopulates state.
  useEffect(() => {
    if (!registrationsData) return;
    setAllRegistrations((prev) =>
      registrationsOffset === 0 ? registrationsData.data : [...prev, ...registrationsData.data]
    );
  }, [registrationsData, registrationsOffset]);

  useEffect(() => {
    if (!inviteesData) return;
    setAllInvitees((prev) =>
      inviteesOffset === 0 ? inviteesData.data : [...prev, ...inviteesData.data]
    );
  }, [inviteesData, inviteesOffset]);

  const registrations = allRegistrations;
  const invitees = allInvitees;
  const shownCount = registrations.length + invitees.length;
  const isLoading = (registrationsLoading || inviteesLoading) && shownCount === 0;
  const isFetchingMore = registrationsFetching || inviteesFetching;
  const registrationsTotal = registrationsData?.total ?? 0;
  const inviteesTotal = inviteesData?.total ?? 0;
  const totalCount = registrationsTotal + inviteesTotal;
  const hasMore = registrations.length < registrationsTotal || invitees.length < inviteesTotal;
  const status = statusData ?? [];
  const organicCount = registrationsData?.organic_count ?? 0;
  const appInviteeCount = registrationsData?.invited_count ?? 0;
  const additionalGuestCount = inviteesData?.additional_guest_count ?? 0;
  const externalInviteeCount = inviteesTotal - additionalGuestCount;
  const organicCheckedInCount = registrationsData?.organic_checked_in_count ?? 0;
  const appInviteeCheckedInCount = registrationsData?.invited_checked_in_count ?? 0;
  const additionalGuestCheckedInCount = inviteesData?.additional_guest_checked_in_count ?? 0;
  const externalCheckedInCount =
    (inviteesData?.checked_in_count ?? 0) - additionalGuestCheckedInCount;
  const checkedInTotal =
    organicCheckedInCount +
    appInviteeCheckedInCount +
    externalCheckedInCount +
    additionalGuestCheckedInCount;

  const guestRows: (
    | { kind: "registration"; data: EventRegistration }
    | { kind: "invitee"; data: EventInvitee }
  )[] = [
    ...registrations.map((r) => ({ kind: "registration" as const, data: r })),
    ...invitees.map((i) => ({ kind: "invitee" as const, data: i })),
  ].sort((a, b) => {
    const aTime = a.data.checked_in_at;
    const bTime = b.data.checked_in_at;
    if (aTime && bTime) return new Date(bTime).getTime() - new Date(aTime).getTime();
    if (aTime && !bTime) return -1;
    if (!aTime && bTime) return 1;
    return 0;
  });

  function handleLoadMore() {
    if (registrations.length < registrationsTotal)
      setRegistrationsOffset((prev) => prev + PAGE_SIZE);
    if (invitees.length < inviteesTotal) setInviteesOffset((prev) => prev + PAGE_SIZE);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg px-2.5 md:px-6">
        <DialogHeader>
          <DialogTitle>Guestlist — {event?.title}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="guests">
          <TabsList>
            <TabsTrigger value="guests">Guests</TabsTrigger>
            <TabsTrigger value="by-inviter">Invite Status</TabsTrigger>
          </TabsList>

          <TabsContent value="guests" className="mt-3 space-y-3">
            {isLoading && (
              <div className="flex justify-center py-8">
                <Spinner className="size-5 border-primary" />
              </div>
            )}

            {!isLoading && shownCount === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">No guests yet.</p>
            )}

            {!isLoading && shownCount > 0 && (
              <>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    {totalCount} guest{totalCount !== 1 ? "s" : ""}
                    <span className="block sm:inline sm:ml-1">
                      ({organicCount} self + {appInviteeCount} app invite + {externalInviteeCount}{" "}
                      public invite + {additionalGuestCount} additional)
                    </span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {checkedInTotal} checked in
                    <span className="block sm:inline sm:ml-1">
                      ({organicCheckedInCount} self + {appInviteeCheckedInCount} app invite +{" "}
                      {externalCheckedInCount} public invite + {additionalGuestCheckedInCount}{" "}
                      additional)
                    </span>
                  </p>
                </div>
                <div className="border rounded-lg divide-y">
                  {guestRows.map((row) => {
                    const guestType =
                      row.kind === "registration"
                        ? row.data.inviter_token_id
                          ? "App Invite"
                          : "Self"
                        : (row.data.guest_number ?? 1) > 1
                          ? "Additional"
                          : "Public Invite";
                    return (
                      <div
                        key={row.data.id}
                        className="flex items-center gap-2 px-2.5 md:px-4 py-3"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium">
                              {row.data.first_name} {row.data.last_name}
                            </p>
                            <Badge
                              variant="outline"
                              className="text-[10px] font-medium px-1.5 py-0.5 bg-gray-100 text-gray-600 border-gray-200 shrink-0"
                            >
                              {guestType}
                            </Badge>
                            {row.kind === "registration" && row.data.expected_guests != null && (
                              <Badge
                                variant="outline"
                                className="text-[10px] font-medium px-1.5 py-0.5 bg-gray-100 text-gray-600 border-gray-200 shrink-0"
                              >
                                {row.data.expected_guests} expected
                              </Badge>
                            )}
                          </div>
                          {row.kind === "registration" ? (
                            <p className="text-xs text-muted-foreground font-mono">
                              {row.data.user_uid}
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">{row.data.email}</p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {row.data.checked_in_at
                              ? `Checked in ${formatDateTimeSmart(row.data.checked_in_at)}`
                              : `Registered ${formatDateTimeSmart(
                                  row.kind === "registration"
                                    ? row.data.registered_at
                                    : row.data.created_at
                                )}`}
                          </p>
                        </div>
                        {row.data.checked_in_at ? (
                          <div className="flex items-center gap-1 text-green-600 text-xs font-medium shrink-0">
                            <CheckCircle className="size-4" />
                            Checked in
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-muted-foreground text-xs shrink-0">
                            <Clock className="size-4" />
                            Pending
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {hasMore && (
                  <div className="flex justify-center pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLoadMore}
                      disabled={isFetchingMore}
                    >
                      {isFetchingMore ? (
                        <>
                          <Spinner className="size-4 border-primary mr-2" />
                          Loading...
                        </>
                      ) : (
                        "Load more"
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="by-inviter" className="mt-3 space-y-3">
            {statusLoading && (
              <div className="flex justify-center py-8">
                <Spinner className="size-5 border-primary" />
              </div>
            )}

            {!statusLoading && status.length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No one has invited any guests yet.
              </p>
            )}

            {!statusLoading && status.length > 0 && (
              <div className="border rounded-lg divide-y">
                {status.map((s) => (
                  <div key={s.inviter_uid} className="flex items-center gap-2 px-2.5 md:px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">
                        {s.first_name} {s.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground font-mono">{s.inviter_uid}</p>
                    </div>
                    <div className="text-xs text-muted-foreground text-right shrink-0 space-y-0.5">
                      <p className="flex items-center justify-end gap-2">
                        <span>DISCA</span>
                        <span className="flex items-center gap-0.5">
                          <Users className="size-3" />
                          {s.app_total_count}
                        </span>
                        <span className="flex items-center gap-0.5 text-green-600">
                          <CheckCircle className="size-3" />
                          {s.app_checked_in_count}
                        </span>
                      </p>
                      <p className="flex items-center justify-end gap-2">
                        <span>Public</span>
                        <span className="flex items-center gap-0.5">
                          <Users className="size-3" />
                          {s.external_total_count}
                        </span>
                        <span className="flex items-center gap-0.5 text-green-600">
                          <CheckCircle className="size-3" />
                          {s.external_checked_in_count}
                        </span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
